"use client"

import Image from "next/image"
import { useTranslations } from "next-intl"

import { moneyShort } from "@/components/marketplace/format"
import { LISTING_PILL_CLASS } from "@/components/marketplace/listing/listing-control-styles"
import { matchesPricePreset } from "@/components/marketplace/listing/price-presets"
import { Button, Pill } from "@/components/ui"
import { facetValueLabel, withAttribute } from "@/lib/types/search"
import type { SearchFacet, SearchQuery } from "@/lib/types/search"

interface ActiveFiltersProps {
  query: SearchQuery
  categoryLabels?: Record<string, string>
  facets?: SearchFacet[]
  onUpdateQuery: (updates: Partial<SearchQuery>) => void
  onClearAll: () => void
  className?: string
}

interface FilterToken {
  key: string
  label: string
  onRemove: () => void
}

export const ActiveFilters = ({ query, categoryLabels, facets, onUpdateQuery, onClearAll, className }: ActiveFiltersProps) => {
  const t = useTranslations("search")
  const tokens: FilterToken[] = []

  query.categories.forEach((id) =>
    tokens.push({
      key: `cat-${id}`,
      label: categoryLabels?.[id] ?? t("activeFilters.categoryFallback"),
      onRemove: () => onUpdateQuery({ categories: query.categories.filter((value) => value !== id) }),
    })
  )

  Object.entries(query.attributes).forEach(([key, values]) =>
    values.forEach((value) =>
      tokens.push({
        key: `${key}-${value}`,
        label: facetValueLabel(facets, key, value),
        onRemove: () =>
          onUpdateQuery({
            attributes: withAttribute(
              query.attributes,
              key,
              values.filter((entry) => entry !== value)
            ),
          }),
      })
    )
  )

  // A preset price range and in-stock are already shown as selected quick-filter
  // pills in the bar; only surface a price token here for a custom range set inside
  // the sheet, so the same filter never appears twice.
  const hasPrice = query.priceMin !== undefined || query.priceMax !== undefined
  if (hasPrice && !matchesPricePreset(query)) {
    const low = query.priceMin !== undefined ? moneyShort(query.priceMin) : null
    const high = query.priceMax !== undefined ? moneyShort(query.priceMax) : null
    const label = low && high ? `${low} – ${high}` : low ? t("activeFilters.priceFrom", { low }) : t("activeFilters.priceTo", { high: high ?? "" })
    tokens.push({ key: "price", label, onRemove: () => onUpdateQuery({ priceMin: undefined, priceMax: undefined }) })
  }

  if (tokens.length === 0) return null

  return (
    <div className={`flex flex-wrap items-center gap-200 ${className ?? ""}`}>
      {tokens.map((token) => (
        <Pill
          key={token.key}
          tone="brand"
          size="sm"
          onClick={token.onRemove}
          className={`${LISTING_PILL_CLASS} border-line-brand bg-surface border`}
          aria-label={t("activeFilters.removeAria", { label: token.label })}
        >
          {token.label}
          <span className="bg-brand flex h-4 w-6.5 shrink-0 items-center justify-center rounded-full px-200">
            <Image src="/images/icons/filter-remove.svg" alt="" width={8} height={8} className="shrink-0" />
          </span>
        </Pill>
      ))}
      <Button variant="quiet" onClick={onClearAll} className="text-fg-muted text-caption-lg shrink-0 font-semibold underline">
        {t("activeFilters.clearAll")}
      </Button>
    </div>
  )
}
